/**
 * AttachmentRail — the two attachment chip rows rendered above the chat input.
 *
 * Extracted verbatim from ChatBox.tsx (Phase 3 leaf extraction). Presentational
 * only: renders the large-paste attachment chips (collapsible) and the inline
 * pending-attachment previews (images + file chips). All state lives in ChatBox;
 * this component receives the lists + their setters and renders both blocks.
 *
 * See ChatBox.tsx `pastedAttachments` / `pendingAttachments` for the source of
 * truth and `handlePaste` / `PASTE_ATTACHMENT_THRESHOLD_BYTES` for how the
 * pasted chips are produced.
 */
import { type Dispatch, type SetStateAction } from 'react';
import type { PastedAttachment, PendingAttachment } from './chatbox/types';

export interface AttachmentRailProps {
  pastedAttachments: PastedAttachment[];
  expandedPastedAttachmentIds: Set<string>;
  setExpandedPastedAttachmentIds: Dispatch<SetStateAction<Set<string>>>;
  setPastedAttachments: Dispatch<SetStateAction<PastedAttachment[]>>;
  pendingAttachments: PendingAttachment[];
  setPendingAttachments: Dispatch<SetStateAction<PendingAttachment[]>>;
  isImporting: boolean;
}

export function AttachmentRail({
  pastedAttachments,
  expandedPastedAttachmentIds,
  setExpandedPastedAttachmentIds,
  setPastedAttachments,
  pendingAttachments,
  setPendingAttachments,
  isImporting,
}: AttachmentRailProps) {
  return (
    <>
      {/* Large-paste attachment chips — see handlePaste +
          PASTE_ATTACHMENT_THRESHOLD_BYTES. Each chip collapses the
          pasted content so it doesn't balloon the textarea; the
          content is sent as `pastedAttachments` on the chat request. */}
      {pastedAttachments.length > 0 && (
        <div
          className="chat-pasted-attachments"
          data-testid="chat-pasted-attachments"
        >
          {pastedAttachments.map(att => {
            const expanded = expandedPastedAttachmentIds.has(att.id);
            return (
              <div
                key={att.id}
                className={`chat-pasted-attachment-chip${expanded ? ' expanded' : ''}`}
                data-testid={`chat-pasted-attachment-${att.id}`}
              >
                <div className="chat-pasted-attachment-header">
                  <svg
                    className="chat-pasted-attachment-icon"
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                  </svg>
                  <span className="chat-pasted-attachment-label">{att.label}</span>
                  <button
                    type="button"
                    className="chat-pasted-attachment-toggle"
                    onClick={() => {
                      setExpandedPastedAttachmentIds(prev => {
                        const next = new Set(prev);
                        if (next.has(att.id)) next.delete(att.id);
                        else next.add(att.id);
                        return next;
                      });
                    }}
                    aria-label={expanded ? 'Collapse pasted content' : 'Expand pasted content'}
                    data-testid={`chat-pasted-attachment-toggle-${att.id}`}
                  >
                    {expanded ? 'Hide' : 'Show'}
                  </button>
                  <button
                    type="button"
                    className="chat-pasted-attachment-remove"
                    onClick={() => {
                      setPastedAttachments(prev => prev.filter(a => a.id !== att.id));
                      setExpandedPastedAttachmentIds(prev => {
                        if (!prev.has(att.id)) return prev;
                        const next = new Set(prev);
                        next.delete(att.id);
                        return next;
                      });
                    }}
                    aria-label="Remove pasted content"
                    data-testid={`chat-pasted-attachment-remove-${att.id}`}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="12"
                      height="12"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      aria-hidden="true"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
                {expanded && (
                  <pre className="chat-pasted-attachment-body">{att.content}</pre>
                )}
              </div>
            );
          })}
        </div>
      )}
      {/* Inline attachment previews */}
      {/* Note: there used to be a CSV-import "pill" rendered above the
          attachment preview. It's been removed in favor of an in-chat
          quick-action chip the agent emits after the user sends the CSV
          (kind: "contacts.import-csv"). The chip handler looks up the
          sent file via recentlySentFilesRef and dispatches the same
          xavior:contacts-import-csv event the modal already listens for. */}
      {pendingAttachments.length > 0 && (
        <div className="chat-attachments-preview">
          {pendingAttachments.map(att => (
            att.previewUrl ? (
              <div key={att.id} className="chat-attachment-image">
                <img src={att.previewUrl} className="chat-attachment-image-preview" alt={att.filename} />
                {isImporting && <span className="chat-attachment-loading chat-attachment-image-loading" aria-hidden="true" />}
                <button
                  type="button"
                  className="chat-attachment-remove chat-attachment-image-remove"
                  onClick={() => {
                    URL.revokeObjectURL(att.previewUrl!);
                    setPendingAttachments(prev => prev.filter(a => a.id !== att.id));
                  }}
                  aria-label="Remove attachment"
                >
                  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
            ) : (
              <div key={att.id} className="chat-attachment-chip">
                <svg className="chat-attachment-file-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                </svg>
                <span className="chat-attachment-name">{att.filename}</span>
                {isImporting && <span className="chat-attachment-loading" aria-hidden="true" />}
                <button
                  type="button"
                  className="chat-attachment-remove"
                  onClick={() => {
                    setPendingAttachments(prev => prev.filter(a => a.id !== att.id));
                  }}
                  aria-label="Remove attachment"
                >
                  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
            )
          ))}
        </div>
      )}
    </>
  );
}
